import { useQuery } from "@apollo/client";
import { Delete, Edit, FileCopy } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { ReactElement, useState } from "react";

import {
  DeleteComplianceBundleMutation,
  DeleteComplianceBundleMutationVariables,
  ListComplianceBundlesQuery,
  ListTenantsQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import Table from "~frontend/components/Table";
import {
  DELETE_COMPLIANCE_BUNDLE,
  LIST_COMPLIANCE_BUNDLES,
} from "~frontend/graphql/complianceBundles";
import { LIST_TENANTS } from "~frontend/graphql/tenant";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { BundleDialog } from "./BundleDialog";

export type BundleTableRow =
  ListComplianceBundlesQuery["listComplianceBundles"]["bundles"][0];

const BundleLink = ({
  path,
  text,
}: {
  path: string;
  text: string;
}): ReactElement => {
  return (
    <FlexBox>
      <ExternalLink
        href={path}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: palette.blue,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
            color: palette.darkBlue,
          },
        }}
      >
        <FileCopy sx={{ fontSize: "18px" }} /> {text}
      </ExternalLink>
    </FlexBox>
  );
};

export default function BundleTable({
  rows,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  totalCount,
}: {
  rows: BundleTableRow[];
  page: number;
  handleChangePage: (newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
}): ReactElement {
  const [editingBundle, setEditingBundle] = useState<BundleTableRow | null>(
    null
  );

  const {
    loading,
    error,
    data: tenantsData,
  } = useQuery<ListTenantsQuery>(LIST_TENANTS);

  const [deleteBundle] = useMutation<
    DeleteComplianceBundleMutation,
    DeleteComplianceBundleMutationVariables
  >(DELETE_COMPLIANCE_BUNDLE, {
    refetchQueries: [LIST_COMPLIANCE_BUNDLES],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error deleting bundle",
  });
  const handleDeleteBundle = async (bundle: BundleTableRow) => {
    await deleteBundle({
      variables: { input: { id: bundle.id } },
    });
  };

  return (
    <>
      <BundleDialog
        open={!!editingBundle}
        onClose={async () => {
          setEditingBundle(null);
        }}
        bundle={editingBundle}
      />
      <Table<BundleTableRow>
        columns={[
          {
            key: "orgName",
            title: "Org Legal Name",
            renderValue: bundle => (
              <>
                <Box
                  data-testid="bundle-orgName-cell"
                  sx={{
                    color: bundle.deletedAt ? palette.darkGray : palette.navy,
                  }}
                >
                  {bundle.orgName}
                  <Typography variant="body3">
                    {bundle.deletedAt ? " (inactive)" : ""}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body3" color={palette.navy}>
                    Created: {new Date(bundle.createdAt).toLocaleDateString()}{" "}
                    by {bundle.createdByUser.fullName}
                  </Typography>
                </Box>
                {bundle.deletedAt ? (
                  <Box>
                    <Typography variant="body3" color={palette.navy}>
                      Deleted: {new Date(bundle.deletedAt).toLocaleDateString()}{" "}
                      by {bundle.deletedByUser!.fullName}
                    </Typography>
                  </Box>
                ) : null}
              </>
            ),
            align: "left",
          },
          {
            key: "tenDlcCampaignId",
            title: "10DLC Campaign ID",
            renderValue: bundle => (
              <span data-testid="bundle-10dlcId-cell">
                {bundle.tenDlcCampaignId ?? "--"}
              </span>
            ),
            align: "left",
          },
          {
            key: "slug",
            title: "Slug",
            renderValue: bundle => (
              <span data-testid="bundle-slug-cell">/{bundle.slug}</span>
            ),
            align: "left",
          },
          {
            key: "bundleTenants",
            title: "Tenants",
            align: "left",
            renderValue: bundle => (
              <AsyncWrapper data={tenantsData} loading={loading} errors={error}>
                {({ listTenants }) => (
                  <>
                    {bundle.bundleTenants.map(bundleTenant => {
                      const tenant = listTenants.find(
                        t => t.id === bundleTenant.tenantId
                      );
                      if (tenant && !bundleTenant.deletedAt) {
                        return <Box key={tenant.id}>{tenant.name}</Box>;
                      }
                      return null;
                    })}
                  </>
                )}
              </AsyncWrapper>
            ),
          },
          {
            key: "id",
            title: "Bundle Links",
            align: "left",
            renderValue: bundle => (
              <FlexBox direction="column" gap={1}>
                <BundleLink
                  text="Sign Up"
                  path={`${bundle.domain}public/${bundle.slug}/sign-up`}
                />
                <BundleLink
                  text="Privacy Policy"
                  path={`${bundle.domain}public/${bundle.slug}/privacy`}
                />
                <BundleLink
                  text="Terms and Conditions"
                  path={`${bundle.domain}public/${bundle.slug}/terms`}
                />
              </FlexBox>
            ),
          },
        ]}
        rows={rows}
        rowActions={row => [
          {
            id: "edit-bundle",
            type: "button",
            icon: <Edit />,
            tooltipLabel: "Edit Bundle",
            onClick: bundle => {
              setEditingBundle(bundle);
            },
          },
          {
            id: "delete-bundle",
            type: "button",
            icon: <Delete />,
            tooltipLabel: "Delete Bundle",
            disabled: !!row.deletedAt,
            confirm: row => ({
              title: `Delete bundle?`,
              description: `Permanently delete bundle for ${row.orgName}?`,
            }),
            onClick: handleDeleteBundle,
          },
        ]}
        emptyState="No bundles found"
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        onChangePage={handleChangePage}
      />
    </>
  );
}
